import React from "react";
import { Button, Tooltip } from "antd";
import {
  CopyOutlined,
  CheckOutlined,
  LikeOutlined,
  LikeFilled,
  DislikeOutlined,
  DislikeFilled,
  ShareAltOutlined,
  ReloadOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { askAiTokens as t } from "./tokens";
import { AskAiMessage } from "./types";
import { useIsMobile } from "./useMediaQuery";

type Feedback = "good" | "bad" | undefined;

interface Props {
  message: AskAiMessage;
  copied: boolean;
  feedback: Feedback;
  showRegenerate: boolean;
  onCopy: () => void;
  onFeedback: (value: "good" | "bad") => void;
  onShare: () => void;
  onRegenerate: () => void;
  onDownloadImage?: () => void;
}

const btnStyle = (
  active: boolean,
  activeColor: string,
  isMobile: boolean
): React.CSSProperties => ({
  color: active ? activeColor : t.color.textMuted,
  height: isMobile ? 40 : 30,
  width: isMobile ? 40 : 30,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
});

const MessageActions: React.FC<Props> = ({
  message,
  copied,
  feedback,
  showRegenerate,
  onCopy,
  onFeedback,
  onShare,
  onRegenerate,
  onDownloadImage,
}) => {
  const isMobile = useIsMobile();
  return (
    <div
      className="askai-message-actions"
      style={{
        display: "flex",
        gap: 4,
        marginTop: t.space.s3,
        // Always visible — discoverable like Claude/ChatGPT.
        opacity: 1,
      }}
    >
      <Tooltip title={copied ? "Copied" : "Copy"}>
        <Button
          type="text"
          size="small"
          icon={
            copied ? (
              <CheckOutlined style={{ color: t.color.success }} />
            ) : (
              <CopyOutlined />
            )
          }
          onClick={onCopy}
          style={btnStyle(false, t.color.textMuted, isMobile)}
        />
      </Tooltip>
      {showRegenerate && (
        <Tooltip title="Regenerate response">
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            onClick={onRegenerate}
            style={btnStyle(false, t.color.textMuted, isMobile)}
          />
        </Tooltip>
      )}
      <Tooltip title="Good response">
        <Button
          type="text"
          size="small"
          icon={
            feedback === "good" ? (
              <LikeFilled style={{ color: t.color.success }} />
            ) : (
              <LikeOutlined />
            )
          }
          onClick={() => onFeedback("good")}
          style={btnStyle(feedback === "good", t.color.success, isMobile)}
        />
      </Tooltip>
      <Tooltip title="Bad response">
        <Button
          type="text"
          size="small"
          icon={
            feedback === "bad" ? (
              <DislikeFilled style={{ color: t.color.danger }} />
            ) : (
              <DislikeOutlined />
            )
          }
          onClick={() => onFeedback("bad")}
          style={btnStyle(feedback === "bad", t.color.danger, isMobile)}
        />
      </Tooltip>
      <Tooltip title="Share">
        <Button
          type="text"
          size="small"
          icon={<ShareAltOutlined />}
          onClick={onShare}
          style={btnStyle(false, t.color.textMuted, isMobile)}
        />
      </Tooltip>
      {message.imageUrl && onDownloadImage && (
        <Tooltip title="Download image">
          <Button
            type="text"
            size="small"
            icon={<DownloadOutlined />}
            onClick={onDownloadImage}
            style={btnStyle(false, t.color.textMuted, isMobile)}
          />
        </Tooltip>
      )}
    </div>
  );
};

export default MessageActions;
